import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Package, X, AlertCircle, Calculator, Calendar } from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../utils/auth';
import { isFilledNumber, missingRequiredMessage } from '../utils/requiredFields';
import Pagination, { usePagination } from './Pagination';
import StyledDropdown from './StyledDropdown';
import DeleteImpactModal from './DeleteImpactModal';

const ProductManagement = ({ onProductCreated }) => {
  const [products, setProducts] = useState([]);
  const [commissionRatios, setCommissionRatios] = useState([]);
  const [thresholds, setThresholds] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupOverrides, setGroupOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showProductDetailsPrompt, setShowProductDetailsPrompt] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    maxCommPercentage: '',
    commissionRatioId: '',
    groupLeaderComm: '',
    teamLeaderComm: '',
    agentComm: '',
    thresholdId: '',
    startDate: '',
    endDate: '',
    status: 'Active',
  });
  const [calculatedComms, setCalculatedComms] = useState({
    groupLeaderComm: '',
    teamLeaderComm: '',
    agentComm: '',
  });

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const [productsRes, ratiosRes, thresholdsRes, groupsRes] = await Promise.all([
        fetch(`${getApiUrl()}/api/products`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/commission-ratios`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/campaign-thresholds`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/groups`, { headers: getAuthHeaders() }),
      ]);

      const productsResult = await productsRes.json();
      const ratiosResult = await ratiosRes.json();
      const thresholdsResult = await thresholdsRes.json();
      const groupsResult = await groupsRes.json();

      if (productsResult.success) {
        setProducts(productsResult.data);
      }

      if (ratiosResult.success) {
        setCommissionRatios(ratiosResult.data);
      }

      if (thresholdsResult.success) {
        setThresholds(thresholdsResult.data);
      }

      if (groupsResult.success) {
        setGroups(groupsResult.data);
      }
    } catch (err) {
      console.error('Fetch data error:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Calculate commissions when maxCommPercentage or ratio changes
  useEffect(() => {
    if (formData.maxCommPercentage && formData.commissionRatioId) {
      const ratio = commissionRatios.find((r) => r.id === parseInt(formData.commissionRatioId));
      if (ratio) {
        const maxComm = parseFloat(formData.maxCommPercentage);
        const glComm = maxComm * parseFloat(ratio.groupLeaderRatio);
        const tlComm = maxComm * parseFloat(ratio.teamLeaderRatio);
        const agentComm = maxComm * parseFloat(ratio.agentRatio);

        setCalculatedComms({
          groupLeaderComm: glComm.toFixed(4),
          teamLeaderComm: tlComm.toFixed(4),
          agentComm: agentComm.toFixed(4),
        });

        // Auto-fill if not editing or if values are empty
        if (!editingProduct || !formData.groupLeaderComm) {
          setFormData((prev) => ({
            ...prev,
            groupLeaderComm: glComm.toFixed(4),
            teamLeaderComm: tlComm.toFixed(4),
            agentComm: agentComm.toFixed(4),
          }));
        }
      }
    }
  }, [formData.maxCommPercentage, formData.commissionRatioId, commissionRatios, editingProduct]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toISOString().split('T')[0];
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const missing = missingRequiredMessage([
        { label: 'Product/Campaign Name', value: formData.name },
        { label: 'Max Commission Percentage', value: formData.maxCommPercentage, check: isFilledNumber },
        { label: 'Commission Ratio', value: formData.commissionRatioId },
        { label: 'Group Leader Commission', value: formData.groupLeaderComm, check: isFilledNumber },
        { label: 'Team Leader Commission', value: formData.teamLeaderComm, check: isFilledNumber },
        { label: 'Agent Commission', value: formData.agentComm, check: isFilledNumber },
      ]);
      if (missing) {
        setError(missing);
        return;
      }

      const response = await fetch(`${getApiUrl()}/api/products`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: formData.name,
          maxCommPercentage: parseFloat(formData.maxCommPercentage),
          commissionRatioId: parseInt(formData.commissionRatioId),
          groupLeaderComm: parseFloat(formData.groupLeaderComm),
          teamLeaderComm: parseFloat(formData.teamLeaderComm),
          agentComm: parseFloat(formData.agentComm),
          thresholdId: formData.thresholdId ? parseInt(formData.thresholdId) : null,
          startDate: formData.startDate || null,
          endDate: formData.endDate || null,
          status: formData.status,
          groupCommissions: groupOverrides,
        }),
      });

      const result = await response.json();

      if (result.success) {
        resetForm();
        setShowCreateModal(false);
        fetchData();
        if (onProductCreated && result.data?.id) {
          setShowProductDetailsPrompt(result.data.id);
        }
      } else {
        setError(result.message || 'Failed to create product/campaign');
      }
    } catch (err) {
      console.error('Create product error:', err);
      setError('Unable to connect to server');
    }
  };

  const handleEditClick = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      maxCommPercentage: product.maxCommPercentage.toString(),
      commissionRatioId: product.commissionRatioId.toString(),
      groupLeaderComm: product.groupLeaderComm.toString(),
      teamLeaderComm: product.teamLeaderComm.toString(),
      agentComm: product.agentComm.toString(),
      thresholdId: product.thresholdId ? product.thresholdId.toString() : '',
      startDate: formatDate(product.startDate),
      endDate: formatDate(product.endDate),
      status: product.status,
    });
    setGroupOverrides(
      (product.groupCommissions || []).map((o) => ({
        groupId: String(o.groupId),
        groupLeaderComm: String(o.groupLeaderComm),
        teamLeaderComm: String(o.teamLeaderComm),
        agentComm: String(o.agentComm),
      }))
    );
    setShowEditModal(true);
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const missing = missingRequiredMessage([
        { label: 'Product/Campaign Name', value: formData.name },
        { label: 'Max Commission Percentage', value: formData.maxCommPercentage, check: isFilledNumber },
        { label: 'Commission Ratio', value: formData.commissionRatioId },
        { label: 'Group Leader Commission', value: formData.groupLeaderComm, check: isFilledNumber },
        { label: 'Team Leader Commission', value: formData.teamLeaderComm, check: isFilledNumber },
        { label: 'Agent Commission', value: formData.agentComm, check: isFilledNumber },
      ]);
      if (missing) {
        setError(missing);
        return;
      }

      const response = await fetch(`${getApiUrl()}/api/products/${editingProduct.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: formData.name,
          maxCommPercentage: parseFloat(formData.maxCommPercentage),
          commissionRatioId: parseInt(formData.commissionRatioId),
          groupLeaderComm: parseFloat(formData.groupLeaderComm),
          teamLeaderComm: parseFloat(formData.teamLeaderComm),
          agentComm: parseFloat(formData.agentComm),
          thresholdId: formData.thresholdId ? parseInt(formData.thresholdId) : null,
          startDate: formData.startDate || null,
          endDate: formData.endDate || null,
          status: formData.status,
          groupCommissions: groupOverrides,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setShowEditModal(false);
        setEditingProduct(null);
        resetForm();
        fetchData();
      } else {
        setError(result.message || 'Failed to update product/campaign');
      }
    } catch (err) {
      console.error('Update product error:', err);
      setError('Unable to connect to server');
    }
  };

  const handleDeleteProduct = async (id) => {
    try {
      setError('');
      const response = await fetch(`${getApiUrl()}/api/products/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (result.success) {
        setDeleteConfirm(null);
        fetchData();
      } else {
        setError(result.message || 'Failed to delete product/campaign');
        setDeleteConfirm(null);
      }
    } catch (err) {
      console.error('Delete product error:', err);
      setError('Unable to connect to server');
      setDeleteConfirm(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      maxCommPercentage: '',
      commissionRatioId: '',
      groupLeaderComm: '',
      teamLeaderComm: '',
      agentComm: '',
      thresholdId: '',
      startDate: '',
      endDate: '',
      status: 'Active',
    });
    setCalculatedComms({
      groupLeaderComm: '',
      teamLeaderComm: '',
      agentComm: '',
    });
    setGroupOverrides([]);
  };

  const getStatusBadge = (status) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        status === 'Active' 
          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
      }`}>
        {status}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Pagination
  const {
    currentPage,
    paginatedItems: paginatedProducts,
    totalItems,
    handlePageChange,
  } = usePagination(products, 10);

  const renderModal = (isEdit = false) => {
    const modalTitle = isEdit ? 'Edit Product/Campaign' : 'Create New Product/Campaign';
    const submitLabel = isEdit ? 'Update' : 'Create';
    const onSubmit = isEdit ? handleUpdateProduct : handleCreateProduct;
    const selectedRatio = commissionRatios.find((r) => r.id === parseInt(formData.commissionRatioId));
    const selectedThreshold = thresholds.find((t) => t.id === parseInt(formData.thresholdId));

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="neo-modal p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto transition-colors duration-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text">{modalTitle}</h3>
            <button
              onClick={() => {
                if (isEdit) {
                  setShowEditModal(false);
                  setEditingProduct(null);
                } else {
                  setShowCreateModal(false);
                }
                resetForm();
                setError('');
              }}
              className="text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-dark-text"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={onSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Product/Campaign Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 neo-field dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  placeholder="e.g., Q4 2025 Campaign"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Max Commission Percentage *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.maxCommPercentage}
                  onChange={(e) => setFormData({...formData, maxCommPercentage: e.target.value})}
                  onWheel={(e) => e.target.blur()}
                  className="w-full px-3 py-2 neo-field dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder="e.g., 4.00"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Commission Ratio *
                </label>
                <StyledDropdown
                  options={[
                    { value: '', label: 'Select a ratio' },
                    ...commissionRatios.map(r => ({ 
                      value: r.id.toString(), 
                      label: `${r.name}${r.isActive ? ' (Active)' : ''}` 
                    }))
                  ]}
                  value={formData.commissionRatioId}
                  onChange={(val) => setFormData({...formData, commissionRatioId: val})}
                  placeholder="Select a ratio"
                  className="w-full"
                  required
                />
              </div>

              {/* Calculated Values Reference */}
              {selectedRatio && formData.maxCommPercentage && (
                <div className="md:col-span-2 bg-primary-50 dark:bg-primary-900/20 p-3 rounded-md border border-primary-200 dark:border-primary-800">
                  <div className="flex items-center gap-2 mb-2">
                    <Calculator className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    <span className="text-sm font-medium text-primary-800 dark:text-primary-300">Calculated Values (Reference):</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs text-primary-700 dark:text-primary-300">
                    <div>GL: {calculatedComms.groupLeaderComm}%</div>
                    <div>TL: {calculatedComms.teamLeaderComm}%</div>
                    <div>Agent: {calculatedComms.agentComm}%</div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Group Leader Commission (%) *
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={formData.groupLeaderComm}
                  onChange={(e) => setFormData({...formData, groupLeaderComm: e.target.value})}
                  onWheel={(e) => e.target.blur()}
                  className="w-full px-3 py-2 neo-field dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder="0.4000"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Team Leader Commission (%) *
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={formData.teamLeaderComm}
                  onChange={(e) => setFormData({...formData, teamLeaderComm: e.target.value})}
                  onWheel={(e) => e.target.blur()}
                  className="w-full px-3 py-2 neo-field dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder="1.0000"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Agent Commission (%) *
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={formData.agentComm}
                  onChange={(e) => setFormData({...formData, agentComm: e.target.value})}
                  onWheel={(e) => e.target.blur()}
                  className="w-full px-3 py-2 neo-field dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder="2.6000"
                  required
                />
              </div>

              <div className="md:col-span-2 border-t pt-4 mt-2">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-dark-text">Group-specific tiers (optional)</h4>
                    <p className="text-xs text-gray-500 dark:text-dark-muted mt-1">
                      Leave a group blank to use the default rates above. Use this when the same product pays a different GL / TL / Agent split in another group.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setGroupOverrides((prev) => [...prev, { groupId: '', groupLeaderComm: '', teamLeaderComm: '', agentComm: '' }])}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Add group override
                  </button>
                </div>
                {groupOverrides.length > 0 && (
                  <div className="space-y-3">
                    {groupOverrides.map((row, idx) => {
                      const usedIds = groupOverrides.map((r, i) => (i !== idx ? r.groupId : null)).filter(Boolean);
                      return (
                        <div key={idx} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end neo-inset p-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-dark-muted mb-1">Group</label>
                            <StyledDropdown
                              options={[
                                { value: '', label: 'Select group' },
                                ...groups.filter((g) => !usedIds.includes(String(g.id)) || String(g.id) === row.groupId).map((g) => ({
                                  value: String(g.id),
                                  label: g.name,
                                })),
                              ]}
                              value={row.groupId}
                              onChange={(val) => setGroupOverrides((prev) => prev.map((r, i) => i === idx ? { ...r, groupId: val } : r))}
                              placeholder="Select group"
                              className="w-full"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-dark-muted mb-1">GL %</label>
                            <input
                              type="number"
                              step="0.0001"
                              min="0"
                              value={row.groupLeaderComm}
                              onChange={(e) => setGroupOverrides((prev) => prev.map((r, i) => i === idx ? { ...r, groupLeaderComm: e.target.value } : r))}
                              onWheel={(e) => e.target.blur()}
                              className="w-full px-2 py-2 neo-field dark:text-dark-text text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-dark-muted mb-1">TL %</label>
                            <input
                              type="number"
                              step="0.0001"
                              min="0"
                              value={row.teamLeaderComm}
                              onChange={(e) => setGroupOverrides((prev) => prev.map((r, i) => i === idx ? { ...r, teamLeaderComm: e.target.value } : r))}
                              onWheel={(e) => e.target.blur()}
                              className="w-full px-2 py-2 neo-field dark:text-dark-text text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-dark-muted mb-1">Agent %</label>
                            <input
                              type="number"
                              step="0.0001"
                              min="0"
                              value={row.agentComm}
                              onChange={(e) => setGroupOverrides((prev) => prev.map((r, i) => i === idx ? { ...r, agentComm: e.target.value } : r))}
                              onWheel={(e) => e.target.blur()}
                              className="w-full px-2 py-2 neo-field dark:text-dark-text text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => setGroupOverrides((prev) => prev.filter((_, i) => i !== idx))}
                            className="text-sm text-red-600 dark:text-red-400 hover:underline mb-2"
                          >
                            Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Campaign Period & Threshold Section */}
              <div className="md:col-span-2 border-t pt-4 mt-2">
                <h4 className="text-sm font-medium text-gray-700 dark:text-dark-text mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Campaign Period & Threshold (Optional)
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                      Threshold
                    </label>
                    <StyledDropdown
                      options={[
                        { value: '', label: 'No threshold' },
                        ...thresholds.filter(t => t.status === 'Active').map(t => ({ 
                          value: t.id.toString(), 
                          label: t.name 
                        }))
                      ]}
                      value={formData.thresholdId}
                      onChange={(val) => setFormData({...formData, thresholdId: val})}
                      placeholder="Select threshold"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                      className="w-full px-3 py-2 neo-field dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                      className="w-full px-3 py-2 neo-field dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    />
                  </div>
                </div>

                {/* Show selected threshold details */}
                {selectedThreshold && (
                  <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-md border border-orange-200 dark:border-orange-800">
                    <div className="text-xs text-orange-700 dark:text-orange-300">
                      <span className="font-medium">Selected Threshold:</span> {selectedThreshold.name}
                      <div className="mt-1 grid grid-cols-2 gap-2">
                        <div>TL Override: {formatCurrency(selectedThreshold.tlOverrideThreshold)}</div>
                        <div>GL Override: {formatCurrency(selectedThreshold.glOverrideThreshold)}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {isEdit && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                    Status
                  </label>
                  <StyledDropdown
                    options={[
                      { value: 'Active', label: 'Active' },
                      { value: 'Inactive', label: 'Inactive' },
                    ]}
                    value={formData.status}
                    onChange={(val) => setFormData({ ...formData, status: val })}
                    placeholder="Select status"
                    className="w-full"
                  />
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  if (isEdit) {
                    setShowEditModal(false);
                    setEditingProduct(null);
                  } else {
                    setShowCreateModal(false);
                  }
                  resetForm();
                  setError('');
                }}
                className="px-4 py-2 text-gray-700 dark:text-dark-text neo-btn transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 neo-btn-primary transition-colors duration-200"
              >
                {submitLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 dark:text-primary-400 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-600 dark:text-dark-muted">Loading products/campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Error Message */}
      {error && !showCreateModal && !showEditModal && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Action Button */}
      <div className="flex items-center justify-end mb-6">
        <button
          onClick={() => {
            setShowCreateModal(true);
            resetForm();
            setError('');
          }}
          className="neo-btn-primary px-4 py-2 text-sm font-medium flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Create Product/Campaign</span>
        </button>
      </div>

      {/* Products Table */}
      <div className="neo-raised overflow-hidden transition-colors duration-200">
        <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-200 dark:border-dark-border">
          <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text">All Products/Campaigns</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
            <thead className="bg-gray-50 dark:bg-slate-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Product/Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Max Comm
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Ratio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  GL / TL / Agent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Threshold
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200/70 dark:divide-dark-border">
              {paginatedProducts.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500 dark:text-dark-muted">
                    No products/campaigns found. Create your first one to get started.
                  </td>
                </tr>
              ) : (
                paginatedProducts.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center mr-3">
                          <Package className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div className="text-sm font-medium text-gray-900 dark:text-dark-text">{product.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-dark-text">{product.maxCommPercentage}%</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-dark-text">
                        {product.commissionRatio?.name || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs text-gray-600 dark:text-dark-muted">
                        <span className="text-blue-600 dark:text-blue-400">{product.groupLeaderComm}%</span>
                        {' / '}
                        <span className="text-purple-600 dark:text-purple-400">{product.teamLeaderComm}%</span>
                        {' / '}
                        <span className="text-green-600 dark:text-green-400">{product.agentComm}%</span>
                      </div>
                      {product.groupCommissions?.length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {product.groupCommissions.map((o) => (
                            <div key={o.id || o.groupId} className="text-[11px] text-gray-500 dark:text-dark-muted">
                              {o.group?.name || `Group ${o.groupId}`}:{' '}
                              <span className="text-blue-600 dark:text-blue-400">{o.groupLeaderComm}%</span>
                              {' / '}
                              <span className="text-purple-600 dark:text-purple-400">{o.teamLeaderComm}%</span>
                              {' / '}
                              <span className="text-green-600 dark:text-green-400">{o.agentComm}%</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-600 dark:text-dark-muted">
                        {product.threshold ? (
                          <div>
                            <div className="font-medium text-gray-900 dark:text-dark-text">{product.threshold.name}</div>
                            <div>TL: {formatCurrency(product.threshold.tlOverrideThreshold)}</div>
                            <div>GL: {formatCurrency(product.threshold.glOverrideThreshold)}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400 dark:text-dark-muted">No threshold</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-600 dark:text-dark-muted">
                        {product.startDate || product.endDate ? (
                          <div>
                            <div>{formatDisplayDate(product.startDate)}</div>
                            <div>to {formatDisplayDate(product.endDate)}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400 dark:text-dark-muted">No period set</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(product.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEditClick(product)}
                          className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(product.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={10}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Create Modal */}
      {showCreateModal && renderModal(false)}

      {/* Edit Modal */}
      {showEditModal && renderModal(true)}

      {/* Delete Confirmation */}
      {showProductDetailsPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="neo-modal p-6 w-full max-w-md transition-colors duration-200">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-2">Product created successfully</h3>
            <p className="text-gray-600 dark:text-dark-muted mb-6">
              Adding Product Details is mandatory so investors can enroll. Go to Product Details now?
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowProductDetailsPrompt(null)}
                className="px-4 py-2 text-gray-700 dark:text-dark-text neo-btn transition-colors duration-200"
              >
                Done
              </button>
              <button
                type="button"
                onClick={() => {
                  onProductCreated?.(showProductDetailsPrompt);
                  setShowProductDetailsPrompt(null);
                }}
                className="px-4 py-2 neo-btn-primary transition-colors duration-200"
              >
                Add Product Details
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteImpactModal
        open={!!deleteConfirm}
        entityType="product"
        entityId={deleteConfirm}
        title="Delete product"
        onClose={() => setDeleteConfirm(null)}
        onDeleted={() => { setDeleteConfirm(null); fetchData(); }}
      />
    </div>
  );
};

export default ProductManagement;
